import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArmadaNav } from "@/components/ArmadaNav";
import { RankBadge, TierBadge, RoleBadge } from "@/components/Badges";
import { TESTID } from "@/lib/testIds";
import {
  ShieldCheck, ShieldOff, KeyRound, Smartphone, Copy, CheckCircle2,
  AlertCircle, Sparkles, Lock,
} from "lucide-react";

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [pw, setPw] = useState({ current: "", new1: "", new2: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const flash = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetupData(data);
    } catch (e) { flash("error", formatApiError(e)); }
    finally { setBusy(false); }
  };

  const enableMfa = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/enable", { code: verifyCode });
      flash("ok", "MFA armor activated. Your account is hardened.");
      setSetupData(null);
      setVerifyCode("");
      await refreshUser();
    } catch (e) { flash("error", formatApiError(e)); }
    finally { setBusy(false); }
  };

  const disableMfa = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: disableCode });
      flash("ok", "MFA armor disabled.");
      setDisableCode("");
      await refreshUser();
    } catch (e) { flash("error", formatApiError(e)); }
    finally { setBusy(false); }
  };

  const changePassword = async () => {
    if (pw.new1.length < 8) { flash("error", "Password must be at least 8 characters"); return; }
    if (pw.new1 !== pw.new2) { flash("error", "Passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/password/change", { current_password: pw.current, new_password: pw.new1 });
      flash("ok", "Password updated.");
      setPw({ current: "", new1: "", new2: "" });
    } catch (e) { flash("error", formatApiError(e)); }
    finally { setPwBusy(false); }
  };

  const copyCodes = () => {
    if (!setupData?.backup_codes) return;
    navigator.clipboard.writeText(setupData.backup_codes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-armada-base">
      <ArmadaNav />
      <main className="max-w-[1100px] mx-auto px-6 lg:px-12 py-10">
        <div className="mb-10 animate-fade-up">
          <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-2">PROFILE</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white">
            {user.full_name}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <RoleBadge role={user.role} />
            <RankBadge rank={user.rank} size="lg" />
            <TierBadge tier={user.revenue_tier} />
            {user.callsign && (
              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">
                {user.callsign}
              </span>
            )}
          </div>
        </div>

        {toast && (
          <div className={`mb-4 flex items-start gap-2 p-3 rounded-md text-sm ${
            toast.kind === "error" ? "bg-red-950/40 border border-red-900/50 text-red-200" : "bg-emerald-950/40 border border-emerald-900/50 text-emerald-200"
          }`}>
            {toast.kind === "error" ? <AlertCircle className="w-4 h-4 mt-0.5" /> : <CheckCircle2 className="w-4 h-4 mt-0.5" />}
            <span>{toast.msg}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* MFA — now the primary focus, full width */}
          <div className="glass-strong rounded-xl p-6 lg:col-span-5 animate-fade-up stagger-1" id="mfa-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold">PHASE 1 ARMOR HARDENING · TOTP</div>
                <div className="text-2xl font-black text-white mt-1 tracking-tight">Two-Factor Authentication</div>
              </div>
              {user.mfa_enabled ? (
                <ShieldCheck className="w-8 h-8 text-emerald-400" />
              ) : (
                <ShieldOff className="w-8 h-8 text-zinc-500" />
              )}
            </div>

            {!user.mfa_enabled && !setupData && (
              <div>
                <p className="text-sm text-zinc-400 mb-4 font-light">
                  Secure your hull with Google Authenticator, Authy, or any TOTP-compatible app. A six-digit code will be required at every login.
                </p>
                <button
                  data-testid={TESTID.profileMfaToggle}
                  onClick={startSetup}
                  disabled={busy}
                  className="btn-gold flex items-center gap-2"
                >
                  <Smartphone className="w-4 h-4" />
                  {busy ? "Preparing…" : "Enable Authenticator"}
                </button>
              </div>
            )}

            {setupData && (
              <div className="space-y-5">
                <div className="flex items-center gap-2 text-[10px] tracking-armada text-[#D4AF37] font-bold">
                  <Sparkles className="w-3 h-3" /> STEP 1 — SCAN QR
                </div>
                <div className="flex flex-col md:flex-row gap-5 items-start">
                  <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#D4AF37]/30 inline-block">
                    <img
                      data-testid={TESTID.profileMfaQr}
                      src={`data:image/png;base64,${setupData.qr_code_b64}`}
                      alt="MFA QR Code"
                      className="w-44 h-44"
                    />
                  </div>
                  <div className="flex-1 text-sm space-y-2">
                    <p className="text-zinc-400 font-light">Or enter this secret manually:</p>
                    <div className="font-mono text-xs p-3 rounded bg-black/50 border border-white/10 text-[#D4AF37] break-all">
                      {setupData.secret}
                    </div>
                    <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold pt-2 flex items-center gap-2">
                      <Sparkles className="w-3 h-3" /> BACKUP CODES
                    </div>
                    <p className="text-[11px] text-zinc-500">Save these. Each works once if you lose your device.</p>
                    <div data-testid={TESTID.profileBackupCodes} className="grid grid-cols-2 gap-1.5 mt-2">
                      {setupData.backup_codes.map((c) => (
                        <span key={c} className="font-mono text-[11px] px-2 py-1.5 rounded bg-black/50 border border-white/10 text-zinc-300 text-center">{c}</span>
                      ))}
                    </div>
                    <button onClick={copyCodes} className="text-[11px] text-[#D4AF37] hover:underline flex items-center gap-1 mt-2">
                      {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied ? "Copied" : "Copy codes"}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-[10px] tracking-armada text-[#D4AF37] font-bold pt-2">
                  <Sparkles className="w-3 h-3" /> STEP 2 — VERIFY
                </div>
                <p className="text-sm text-zinc-400">Enter the 6-digit code from your authenticator:</p>
                <div className="flex gap-3">
                  <input
                    data-testid={TESTID.profileMfaCode}
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\s/g, ""))}
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    className="armada-input text-center text-lg tracking-[0.4em] font-mono max-w-[200px]"
                  />
                  <button
                    data-testid={TESTID.profileMfaEnable}
                    onClick={enableMfa}
                    disabled={busy || verifyCode.length < 6}
                    className="btn-gold"
                  >
                    {busy ? "Verifying…" : "Activate Armor"}
                  </button>
                  <button onClick={() => { setSetupData(null); setVerifyCode(""); }} className="btn-ghost-gold">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {user.mfa_enabled && !setupData && (
              <div>
                <div className="p-4 rounded-lg bg-emerald-950/20 border border-emerald-900/30 text-emerald-200 text-sm mb-4 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Your account is armored with TOTP MFA.
                </div>
                <p className="text-sm text-zinc-400 mb-3">To disable, enter your current 6-digit code:</p>
                <div className="flex gap-3">
                  <input
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\s/g, ""))}
                    placeholder="000000"
                    maxLength={9}
                    inputMode="numeric"
                    className="armada-input text-center font-mono max-w-[200px]"
                  />
                  <button
                    data-testid={TESTID.profileMfaDisable}
                    onClick={disableMfa}
                    disabled={busy || disableCode.length < 6}
                    className="btn-ghost-gold"
                  >
                    {busy ? "…" : "Disable MFA"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Identity */}
          <div className="glass-strong rounded-xl p-6 lg:col-span-2 animate-fade-up stagger-2">
            <div className="text-[10px] tracking-armada text-zinc-400 font-bold">IDENTITY</div>
            <div className="text-lg font-bold text-white mt-1 mb-4">Sovereign Profile</div>
            <div className="space-y-2 text-sm">
              <Row k="Email" v={<span className="font-mono text-xs">{user.email}</span>} />
              <Row k="Full Name" v={user.full_name} />
              <Row k="Callsign" v={user.callsign || "—"} />
              <Row k="Role" v={<RoleBadge role={user.role} />} />
              <Row k="Rank" v={<RankBadge rank={user.rank} />} />
              <Row k="Revenue Tier" v={<TierBadge tier={user.revenue_tier} />} />
              <Row k="MFA" v={
                <span className={`inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold ${user.mfa_enabled ? "text-emerald-400" : "text-zinc-500"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${user.mfa_enabled ? "bg-emerald-400" : "bg-zinc-600"}`} />
                  {user.mfa_enabled ? "Armored" : "Unarmored"}
                </span>
              } />
            </div>
            {(user.assigned_hulls || []).length > 0 && (
              <>
                <div className="mt-5 text-[10px] tracking-armada text-zinc-400 font-bold">AUTHORIZED HULLS</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {user.assigned_hulls.map((h) => (
                    <span key={h} className="px-2 py-0.5 rounded text-[11px] bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37]">{h}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Password */}
          <div className="glass-strong rounded-xl p-6 lg:col-span-3 animate-fade-up stagger-3">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-armada text-zinc-400 font-bold">CREDENTIALS</div>
                <div className="text-lg font-bold text-white mt-1">Change Password</div>
              </div>
              <Lock className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                data-testid={TESTID.profileChangePwCurrent}
                type="password"
                placeholder="Current password"
                value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })}
                className="armada-input"
              />
              <input
                data-testid={TESTID.profileChangePwNew}
                type="password"
                placeholder="New password"
                value={pw.new1}
                onChange={(e) => setPw({ ...pw, new1: e.target.value })}
                className="armada-input"
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={pw.new2}
                onChange={(e) => setPw({ ...pw, new2: e.target.value })}
                className="armada-input"
              />
            </div>
            <button
              data-testid={TESTID.profileChangePwSubmit}
              onClick={changePassword}
              disabled={pwBusy || !pw.new1 || !pw.new2}
              className="btn-gold mt-4 flex items-center gap-2"
            >
              <KeyRound className="w-4 h-4" />
              {pwBusy ? "Updating…" : "Update Password"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
      <span className="text-zinc-500 text-xs tracking-wider uppercase">{k}</span>
      <span className="text-white text-sm">{v}</span>
    </div>
  );
}
